/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import React, { Suspense } from "react";
import { Map, OrderedSet } from "immutable";
import { ReduxState, RemoteErrors } from "../src/store/ReduxState";
import { DispatchFunc } from "../src/store/ActionTypes";
import { connect } from "react-redux";
import { RemoteScope, RemoteErrorType } from "./store/RemoteTypes";
import { modalClose, modalShow } from "./utils/redux/ActionTypes";
import {
    Switch,
    Route,
    BrowserRouter,
} from "react-router-dom";
import { PageDisplay, Pages, SubPages, SubPageDisplay} from "./store/AppDisplays";
import { ModalNotification } from "@vwpfs/mary-air-lib/components/air/09-views/03-modals/Notification";
import { ThemePaletteState, ThemePalette } from "@vwpfs/mary-air-lib/theme/_Types";
import { remoteClearError } from "./store/RemoteActions";
import { NoMatchingRoute } from "@vwpfs/mary-air-lib/components/air/09-views/02-pages/NoMatchingRoute";
import { Loading } from "@vwpfs/mary-air-lib/components/air/09-views/02-pages/Loading";
import { HomeNavBar } from "./App/components/09-views/00-blocks/NavBar";
import { SideBar } from "./App/components/09-views/Sidebar";
import { FlexWrapperFocus } from "./components/core/03-base/FlexWrapper";
import { BreakpointConsumer } from "@vwpfs/mary-air-lib/utils/Breakpoints";
import { MaryProvider } from "./utils/redux/MaryProvider";
import { VerifyContextWrapper } from "./App/utils/VerifyContextWrapper";
import { RTE } from "@vwpfs/mary-air-lib/components/air/07-organisms/RTE";
import { Badge } from "@vwpfs/mary-air-lib/components/air/05-atoms/Badge";
import { FlexRow } from "@vwpfs/mary-air-lib/components/air/03-base/Flex/Row";
import { FlexColumn } from "@vwpfs/mary-air-lib/components/air/03-base/Flex/Column";
import { Blockquote } from "@vwpfs/mary-air-lib/components/air/05-atoms/Blockquote";
import { getText } from "@vwpfs/mary-air-lib/utils/translations/I18n";
import { EnvIndicatorBar } from "@vwpfs/mary-air-lib/components/air/06-molecules/EnvIndicatorBar";
import { KeycloakAuthenticated } from "./App/components/05-atoms/keycloakAuthenticated";
import { PreLoader } from "./components/core/03-base/Preloader";
import { NoRole } from "./App/components/09-views/02-pages/NoRole";

type Props = StateProps & DispatchProps;

/**
 *
 */
interface DispatchProps {
    handleRemoteError: (scope: RemoteScope, error: RemoteErrors) => void;
}

/**
 *
 */
interface StateProps {
    availablePages: OrderedSet<Pages>;
    remoteErrors: Map<RemoteScope, RemoteErrors>;
    isAuthenticated: boolean;
}

const handleRemoteErrorModal = (id: string, onClose: () => void, error: RemoteErrors) => {
    const modal = (
        <ModalNotification
            refreshButton={error.type === RemoteErrorType.AUTHORIZATION}
            id={id}
            title={error.type === RemoteErrorType.VALIDATION
                ? getText("app.notification", "Notification")
                : getText("app.warning", "Warning")}
            onCloseModal={onClose}
            theme={{
                paletteState: error.type !== RemoteErrorType.VALIDATION
                    ? error.type === RemoteErrorType.UNKNOWN_ERROR
                        || error.type === RemoteErrorType.DEFAULT
                        ? ThemePaletteState.DANGER : ThemePaletteState.WARNING
                    : undefined,
                palette: error.type === RemoteErrorType.VALIDATION ? ThemePalette.CONTRAST_TERTIARY
                    : undefined,
            }}
        >
            <React.Fragment>
                <Blockquote
                    style={{
                        maxHeight: "200px",
                        overflowY: "scroll",
                        wordWrap: "break-word",
                        whiteSpace: "pre-line",
                    }}
                >
                    <RTE>
                        <h5>
                            {error.type !== RemoteErrorType.UNKNOWN_ERROR ?
                                getText("app.remote-error", "Claude can't continue with your request.") :
                                getText("app.fatal-remote-error", "Even Claude doesn't know what went wrong here!")
                            }
                        </h5>
                        {error.message}
                         {error.path && (
                            <><br/><br/><Badge theme={{ palette: ThemePalette.CONTRAST_TERTIARY }}
                                style={{ marginBottom: "10px" }}>
                            API: {error.path}
                            </Badge></>
                        )}
                    </RTE>
                </Blockquote>
            </React.Fragment>
        </ModalNotification>
    );
    const ModalComponent = () => modal;
    ModalComponent.displayName = 'RemoteErrorModal';
    return ModalComponent;
};

const mapDispatchToProps = (dispatch: DispatchFunc): DispatchProps => ({
    handleRemoteError: (scope, error) => dispatch(
        modalShow("handleremoteerror", handleRemoteErrorModal("handleremoteerror", () => {
            dispatch(modalClose("handleremoteerror"));
            dispatch(remoteClearError(scope));
        }, error))),
});

const mapStateToProps = (state: ReduxState): StateProps => ({
    availablePages: state.prop("pages").prop("pages"),
    remoteErrors: state.prop("remoteErrors"),
    isAuthenticated: !!state.prop("user").isPresent() && !!state.prop("user").get().userRole,
});

class AppComp
    extends React.Component<Props, {}> {

    public constructor(props: Props) {
        super(props);
    }

    public componentDidMount() {
        this.checkRemoteError();
    }

    public componentDidUpdate(_prevProps: Props) {
        this.checkRemoteError();
    }

    /**
     *
     */
    public render() {
        return (
            <Suspense fallback={<Loading/>}>
                <Switch>
                    {
                        this.props.isAuthenticated && (!this.props.availablePages.isEmpty()) ? (
                            [
                            this.props.availablePages.map((page: Pages, index) => ([
                                <Route
                                    key={"route-" + PageDisplay[page].path + index}
                                    path={PageDisplay[page].path}
                                    component={PageDisplay[page].component}
                                    exact
                                />,

                                    PageDisplay[page].subpages.map((subpage: SubPages, subindex) => (
                                    <Route
                                        key={"sub-route-" + SubPageDisplay[subpage].path + subindex}
                                        path={SubPageDisplay[subpage].path}
                                        component={SubPageDisplay[subpage].component}
                                        exact
                                    />
                                ))]
                            )),
                            <Route
                                key={"fallback-no-route"}
                                render={() => (
                                    <NoMatchingRoute
                                        title={this.props.availablePages.isEmpty()
                                                ? "Claude is not initiated yet" : undefined}
                                        description={this.props.availablePages.isEmpty() ?
                                            "It could be that the application is still loading.." : undefined}
                                    />
                                )}
                            />,
                            ]
                    ) :  <Route key={"fallback-no-role"} path="*" exact component={NoRole} />
                }
                </Switch>
            </Suspense>
        );
    }

    private checkRemoteError = () => {
        const errorEntry = this.props.remoteErrors.findEntry((val: RemoteErrors) => !!val);
        console.log("checkRemoteError", errorEntry);
            if (errorEntry) {
                const [scope, reason] = errorEntry;
                if ((reason.type === RemoteErrorType.VALIDATION && scope === RemoteScope.COLLECTIVE_INVOICE_GENERATE) ||
                    (reason.type === RemoteErrorType.VALIDATION && scope === RemoteScope.FILE_UPLOAD) ||
                    (reason.type === RemoteErrorType.VALIDATION
                        && scope === RemoteScope.GENERATE_REPORT_ACCOUNTING_POST)) {
                    return;
                } else {
                    this.props.handleRemoteError(scope, reason);
                }
            }
    };
}

/**
 *
 */

export const AppConnect = connect(
    mapStateToProps,
    mapDispatchToProps,
)(AppComp);

export function App() {
    return (
        <BrowserRouter>
            <KeycloakAuthenticated
                onAuthenticating={<PreLoader force />}
                onNotAllowed={
                    <>
                        {" "}
                        <MaryProvider
                            flexDefaultFocus={FlexWrapperFocus.SIDEBAR_INACTIVE}
                        >
                            <SideBar noRights />
                            <FlexColumn isPage>
                                <BreakpointConsumer>
                                    {(value) =>
                                        !value.breakpointAtSM && (
                                            <FlexRow isFixed>
                                                <HomeNavBar />
                                            </FlexRow>
                                        )
                                    }
                                </BreakpointConsumer>

                                <EnvIndicatorBar />

                                <FlexRow
                                    theme={{
                                        palette: ThemePalette.CONTRAST_SECONDARY,
                                    }}
                                >
                                    <FlexColumn hasScrollable>
                                        <NoMatchingRoute
                                            title="Oops, you're not allowed to come here at all!"
                                            description="You (unfortunately) do not have rights to use the Extended warranty application, we find this very annoying for you. But what now? Never come here again? Or perhaps you would like to be here? If so, request access via FS.Get."
                                        />
                                    </FlexColumn>
                                </FlexRow>
                            </FlexColumn>
                        </MaryProvider>
                    </>
                }
            >
                <MaryProvider flexDefaultFocus={FlexWrapperFocus.SIDEBAR_INACTIVE}>
                    <SideBar />
                    <FlexColumn isPage >
                        <BreakpointConsumer>
                            {(value) => {
                                if (!value.breakpointAtSM) {
                                    return (
                                        <FlexRow isFixed>
                                            <HomeNavBar />
                                        </FlexRow>
                                    );
                                } else {
                                    return null;
                                }
                            }}
                        </BreakpointConsumer>
                        <EnvIndicatorBar />
                        <FlexRow
                            theme={{
                                palette: ThemePalette.CONTRAST_SECONDARY,
                            }}
                            style={{ paddingTop: "20px" }}
                        >
                            <FlexColumn
                                hasScrollable
                            >
                                <VerifyContextWrapper>
                                    <AppConnect />
                                </VerifyContextWrapper>
                            </FlexColumn>
                        </FlexRow>
                    </FlexColumn>
                </MaryProvider>
                </KeycloakAuthenticated>
        </BrowserRouter>
    );
}

export const testUtil = {
    AppComp: AppComp,
    handleRemoteErrorModal: handleRemoteErrorModal,
    mapDispatchToProps: mapDispatchToProps,
    mapStateToProps: mapStateToProps,
};
